import type { Exercise } from "../types";

export const virtuosoExercises: Exercise[] = [
  {
    id: "vi-01",
    levelId: "virtuoso",
    order: 1,
    title: "Notas Ligadas: El Legato Perfecto",
    subtitle: "Unir notas sin rasgar de nuevo: la maravilla del left-hand-only",
    category: "digitacion",
    difficulty: "experto",
    type: "sequence",
    methodRef: "Cap. XVIII - Notas ligadas (Ej. 68-70)",
    description:
      "Una ligadura une dos notas: la primera se rasga, la segunda se produce solamente con la mano izquierda (hammer-on o pull-off). Es la tecnica que da fluidez a los solos y ornamenta el acompañamiento.",
    instructions: [
      "Toca la primera nota con la mano derecha.",
      "La segunda nota se produce solamente golpeando el traste con el dedo (hammer-on).",
      "En sentido inverso: pull-off suelta el dedo para producir la nota inferior.",
    ],
    steps: [
      { id: 1, instruction: "Ligado ascendente (hammer-on): toca Mi (cuerda 6, traste 0)", positions: [{ string: 6, fret: 0 }] },
      { id: 2, instruction: "Sin rasgar de nuevo, golpea Fa con dedo 1 (traste 1)", positions: [{ string: 6, fret: 1 }] },
      { id: 3, instruction: "Desde Fa, hammer-on a Fa# con dedo 2 (traste 2)", positions: [{ string: 6, fret: 2 }] },
      { id: 4, instruction: "Ligado descendente (pull-off): toca Sol (traste 3)", positions: [{ string: 6, fret: 3 }] },
      { id: 5, instruction: "Tira del dedo hacia abajo para sonar Fa# (traste 2)", positions: [{ string: 6, fret: 2 }] },
      { id: 6, instruction: "Pull-off a Fa (traste 1)", positions: [{ string: 6, fret: 1 }] },
    ],
    professorHint:
      "El hammer-on es un golpe rapido del dedo en el traste. El pull-off es un tiron hacia el suelo que hace vibrar la cuerda. Ambos requieren precision, no fuerza.",
    professorNote:
      "El Capitulo XVIII tiene 3 ejercicios de ligados. Son ejercicios cortos pero exigentes: la calidad del sonido depende enteramente de la mano izquierda.",
    timeEstimate: 8,
    stringsNeeded: [6],
    fretRange: [0, 5],
  },
  {
    id: "vi-02",
    levelId: "virtuoso",
    order: 2,
    title: "Terceras y Sextas Melodicas",
    subtitle: "Los intervalos que cantan: ornamentacion del folclor chileno",
    category: "teoria",
    difficulty: "experto",
    type: "sequence",
    methodRef: "Cap. XIX - Practica de terceras y sextas (Ej. 71-75)",
    description:
      "Las terceras y sextas son los intervalos que la musica vocal chilena utiliza para ornamentar las melodias. Tocarlos en la guitarra requiere coordinacion entre ambas manos y un oido fino para la consonancia.",
    instructions: [
      "Toca una escala en intervalos de tercera: nota 1 + nota 3 de la escala.",
      "Cada par de notas forma una consonancia.",
      "Practica tanto ascendente como descendente.",
    ],
    steps: [
      { id: 1, instruction: "Terceras de Do Mayor: Do + Mi (cuerdas 5-4)", positions: [{ string: 5, fret: 3 }, { string: 4, fret: 2 }] },
      { id: 2, instruction: "Re + Fa# (cuerdas 5-4)", positions: [{ string: 5, fret: 5 }, { string: 4, fret: 4 }] },
      { id: 3, instruction: "Mi + Sol (cuerdas 5-4)", positions: [{ string: 5, fret: 7 }, { string: 4, fret: 5 }] },
      { id: 4, instruction: "Fa + La (cuerdas 5-4)", positions: [{ string: 5, fret: 8 }, { string: 4, fret: 7 }] },
      { id: 5, instruction: "Sol + Si (cuerdas 5-4)", positions: [{ string: 5, fret: 10 }, { string: 4, fret: 9 }] },
      { id: 6, instruction: "La + Do (cuerda 5-4, octava)", positions: [{ string: 5, fret: 12 }, { string: 4, fret: 10 }] },
    ],
    professorHint:
      'Las terceras siempre suenan "bien". En el folclor chileno, las melodias se ornamentan con terceras paralelas: es el caracteristico dueto guitarra-voz.',
    professorNote:
      "El Capitulo XIX tiene 5 ejercicios de terceras y sextas. Es la preparacion para tocar a dos voces y para el contrapunto sencillo del repertorio chileno.",
    timeEstimate: 10,
    stringsNeeded: [4, 5],
    fretRange: [0, 15],
  },
  {
    id: "vi-03",
    levelId: "virtuoso",
    order: 3,
    title: "Armónicos Naturales",
    subtitle: "El toque magico: notas cristalinas que flotan sobre el acorde",
    category: "efectos",
    difficulty: "experto",
    type: "click",
    methodRef: "Cap. XX - Efectos especiales: Armónicos naturales (Ej. 76)",
    description:
      "Los armónicos naturales se producen tocando muy suavemente sobre ciertos trastes (5, 7, 12, 17, 19) sin presionar completamente. Son el toque magico que da brillo a la musica chilena.",
    instructions: [
      "Coloca el dedo EXACTAMENTE sobre el traste metalico (no entre trastes).",
      "Toca la cuerda y levanta el dedo inmediatamente.",
      'Escucha la nota "flautada" que emerge.',
    ],
    steps: [
      {
        id: 1,
        instruction: "Armonico de Mi en cuerda 6, traste XII",
        positions: [{ string: 6, fret: 12 }],
        answers: ["Mi"],
        hint: "El traste XII produce la octava exacta",
      },
      { id: 2, instruction: "Armonico de Si en cuerda 5, traste XII", positions: [{ string: 5, fret: 12 }], answers: ["Si"] },
      { id: 3, instruction: "Armonico de Sol en cuerda 3, traste XII", positions: [{ string: 3, fret: 12 }], answers: ["Sol"] },
      {
        id: 4,
        instruction: "Armonico en cuerda 6, traste VII (quinta)",
        positions: [{ string: 6, fret: 7 }],
        answers: ["Si"],
        hint: "El traste VII produce la quinta del armónico",
      },
      {
        id: 5,
        instruction: "Armonico en cuerda 6, traste V (doble octava)",
        positions: [{ string: 6, fret: 5 }],
        answers: ["Mi"],
        hint: "El traste V produce la doble octava",
      },
    ],
    professorHint:
      "Los armónicos solo funcionan en trastes especiales: 5, 7, 12, 17, 19. Toca MUY SUAVE: si aprietas demasiado, suena la nota normal.",
    professorNote:
      'El Capitulo XX llama a los armónicos "efectos especiales". En el repertorio de Enrique Kaliski se utilizan como color expresivo.',
    timeEstimate: 8,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 12],
  },
  {
    id: "vi-04",
    levelId: "virtuoso",
    order: 4,
    title: "Tambora: El Ritmo del Folclor",
    subtitle: "Percusion con la mano derecha: el alma del acompañamiento chileno",
    category: "efectos",
    difficulty: "experto",
    type: "practice",
    methodRef: "Cap. XX - Efectos especiales: Tambora (Ej. 77)",
    description:
      "La tambora es golpear las cuerdas con el lado de la mano derecha para crear un efecto de percusion. Es el golpe caracteristico de la cueca, la tonada y el vals chileno. Sin tambora, no hay folclor.",
    instructions: [
      "Forma un acorde con la mano izquierda.",
      "Con el lado de la mano derecha (hueco de la palma), golpea las cuerdas cerca del puente.",
      "Coordina el golpe con el pulgar que toca el bajo.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Forma La Mayor (posicion abierta)",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }, { string: 1, fret: 2 }],
      },
      {
        id: 2,
        instruction: "Golpe de tambora en el tiempo fuerte (1)",
        positions: [
          { string: 5, fret: 0 },
          { string: 4, fret: 0 },
          { string: 3, fret: 2 },
          { string: 2, fret: 2 },
          { string: 1, fret: 2 },
        ],
      },
      { id: 3, instruction: "Pulgar toca el bajo (La en cuerda 5)", positions: [{ string: 5, fret: 0 }] },
      {
        id: 4,
        instruction: "Tambora en el tiempo 2 + i-m-a en agudas",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }, { string: 1, fret: 2 }],
      },
      {
        id: 5,
        instruction: "Patron completo: tambora-pulgar-tambora-arpegio",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }, { string: 1, fret: 2 }],
      },
    ],
    professorHint:
      'La tambora NO es un golpe fuerte: es un "apagado" ritmico. Practica SIN tocar notas primero.',
    professorNote:
      "El Capitulo XX incluye la tambora como segundo efecto especial. En la Segunda Parte del metodo, la tambora es esencial para los ritmos de cueca, trote y pericona.",
    timeEstimate: 10,
    stringsNeeded: [1, 2, 3, 4, 5],
    fretRange: [0, 3],
  },
  {
    id: "vi-05",
    levelId: "virtuoso",
    order: 5,
    title: "El Gran Recorrido",
    subtitle: "Une todo: escala cromatica completa del traste 0 al XII con todas las tecnicas",
    category: "escalas",
    difficulty: "experto",
    type: "sequence",
    methodRef: "Cap. XIV + XVII + XX - Consolidacion final",
    description:
      "Este ejercicio final une TODAS las tecnicas del gimnasio: digitacion con apoyo, desplazamientos, ligados, ceja y equisonos. Es el examen final que demuestra que tienes el mastil completamente cartografiado.",
    instructions: [
      "Sube por el mastil usando todas las cuerdas.",
      "Aplica ligados, desplazamientos y cambios de cuerda.",
      "Llega hasta el traste XII y vuelve al origen.",
    ],
    steps: [
      { id: 1, instruction: "Inicio: Mi grave (cuerda 6, traste 0)", positions: [{ string: 6, fret: 0 }] },
      { id: 2, instruction: "Sube cromatica por cuerda 6 hasta traste 5 (La)", positions: [{ string: 6, fret: 5 }] },
      { id: 3, instruction: "Cambia a cuerda 5, traste 0 (La) - equisono", positions: [{ string: 5, fret: 0 }] },
      { id: 4, instruction: "Continua cromatica por cuerda 5 hasta traste 5 (Re)", positions: [{ string: 5, fret: 5 }] },
      { id: 5, instruction: "Cambia a cuerda 4, traste 0 (Re) - equisono", positions: [{ string: 4, fret: 0 }] },
      { id: 6, instruction: "Sigue hasta traste 5 (Sol)", positions: [{ string: 4, fret: 5 }] },
      { id: 7, instruction: "Cambia a cuerda 3, traste 0 (Sol) - equisono", positions: [{ string: 3, fret: 0 }] },
      { id: 8, instruction: "Hasta traste 4 (Si)", positions: [{ string: 3, fret: 4 }] },
      { id: 9, instruction: "Cambia a cuerda 2, traste 0 (Si) - equisono", positions: [{ string: 2, fret: 0 }] },
      { id: 10, instruction: "Final: hasta cuerda 1, traste 0 (Mi agudo)", positions: [{ string: 1, fret: 0 }] },
    ],
    professorHint:
      "Este ejercicio usa los equisonos (Cap. XIII) para cambiar de cuerda sin interrumpir la linea cromatica. Es la demostracion de que el mastil es UN SOLO instrumento, no 6 cuerdas separadas.",
    professorNote:
      "El metodo chileno culmina con la consolidacion de todos los capitulos. Este ejercicio es propio del gimnasio Bordona: un recorrido completo que valida el dominio de todo el sistema.",
    timeEstimate: 12,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 12],
  },
];
